import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { createBasePinEl, createPhotoEl, updatePinElement } from './mapMarkers';
import type { MarkerPreceptor } from './types';

interface UseMapMarkersOptions {
  mapRef: MutableRefObject<mapboxgl.Map | null>;
  mapLoaded: boolean;
  preceptors: MarkerPreceptor[];
  selectedId?: string;
  showPhotos: boolean;
  photoSize: number;
  pinStyle: 'badge' | 'photo' | 'dot' | 'pin';
  markerColor: string;
  showIndices: boolean;
  radiusMiles?: number;
  currentZoom: number;
  clusterCentroidMode: boolean;
  clusterLabel?: string;
  losAngelesOnly: boolean;
  centerOverride?: [number, number];
  zipBounds?: [number, number, number, number];
  userInteractedRef: MutableRefObject<boolean>;
  autoFit: boolean;
  detailView: boolean;
  freezeZoom: boolean;
  fixedZoomLevel?: number;
  disablePan: boolean;
  userLocation?: { lat: number; lng: number };
  extraPins: { id?: string; lng: number; lat: number; color?: string; label?: string }[];
  onMarkerClick?: (id: string) => void;
  showTooltipForPreceptor?: (p: MarkerPreceptor, options?: { hideName?: boolean }) => void;
  scheduleHideTooltip?: () => void;
  removeRadiusLayers: () => void;
  renderRadiusLayers: (options: {
    preceptors: MarkerPreceptor[];
    radiusMiles: number;
    selectedId?: string;
    onMarkerClick?: (id: string) => void;
    showTooltipForPreceptor?: (p: MarkerPreceptor, options?: { hideName?: boolean }) => void;
    scheduleHideTooltip?: () => void;
  }) => void;
}

export const useMapMarkers = ({
  mapRef,
  mapLoaded,
  preceptors,
  selectedId,
  showPhotos,
  photoSize,
  pinStyle,
  markerColor,
  showIndices,
  radiusMiles,
  currentZoom,
  clusterCentroidMode,
  clusterLabel,
  losAngelesOnly,
  centerOverride,
  zipBounds,
  userInteractedRef,
  autoFit,
  detailView,
  freezeZoom,
  fixedZoomLevel,
  disablePan,
  userLocation,
  extraPins,
  onMarkerClick,
  showTooltipForPreceptor,
  scheduleHideTooltip,
  removeRadiusLayers,
  renderRadiusLayers,
}: UseMapMarkersOptions) => {
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const markerCacheRef = useRef<Map<string, { marker: mapboxgl.Marker; el: HTMLElement }>>(new Map());
  const extraMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const lastMarkerStyleKeyRef = useRef<string>('');
  const lastMarkerStateKeyRef = useRef<string>('');
  const lastSelectedIdRef = useRef<string | undefined>(undefined);

  const validPreceptors = useMemo(() => {
    return preceptors.filter(p => {
      if (typeof p.latitude !== 'number' || typeof p.longitude !== 'number') return false;
      if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) return false;
      return !(p.latitude === 0 && p.longitude === 0);
    });
  }, [preceptors]);

  const markerStyleKey = useMemo(
    () => `${showPhotos}-${photoSize}-${pinStyle}-${markerColor}-${showIndices}`,
    [showPhotos, photoSize, pinStyle, markerColor, showIndices]
  );

  const preceptorRenderKey = useMemo(
    () => validPreceptors.map(p => `${p.id}:${p.latitude}:${p.longitude}`).join('|'),
    [validPreceptors]
  );

  const extraPinsKey = useMemo(
    () => extraPins.map(p => `${p.id ?? ''}:${p.lng}:${p.lat}:${p.color ?? ''}:${p.label ?? ''}`).join('|'),
    [extraPins]
  );

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    const map = mapRef.current;

    const markerStateKey = `${markerStyleKey}|${preceptorRenderKey}|${selectedId ?? ''}|${currentZoom}|${clusterCentroidMode}|${radiusMiles ?? ''}|${losAngelesOnly}|${userLocation ? `${userLocation.lat},${userLocation.lng}` : 'no-user'}|${extraPinsKey}`;
    const hasAnyRenderedMarkers = markerCacheRef.current.size > 0 || markersRef.current.length > 0 || extraMarkersRef.current.length > 0;
    if (lastMarkerStateKeyRef.current === markerStateKey && hasAnyRenderedMarkers) return;
    lastMarkerStateKeyRef.current = markerStateKey;

    if (lastMarkerStyleKeyRef.current !== markerStyleKey) {
      markerCacheRef.current.forEach(entry => entry.marker.remove());
      markerCacheRef.current.clear();
      lastMarkerStyleKeyRef.current = markerStyleKey;
    }

    extraMarkersRef.current.forEach(marker => marker.remove());
    extraMarkersRef.current = [];
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (process.env.NODE_ENV === 'development') {
      console.log('[RotationMap] Preceptors received:', preceptors.length);
      console.log('[RotationMap] Valid preceptors with coordinates:', validPreceptors.length);
      if (preceptors.length > 0 && validPreceptors.length === 0) {
        console.warn('[RotationMap] No valid coordinates found! Sample preceptor data:', preceptors.slice(0, 3).map(p => ({
          id: p.id,
          name: p.name,
          latitude: p.latitude,
          longitude: p.longitude,
          latType: typeof p.latitude,
          lngType: typeof p.longitude,
        })));
      }
    }

    const canRenderRadius = typeof radiusMiles === 'number' && radiusMiles > 0 && !clusterCentroidMode && !losAngelesOnly;
    const RADIUS_ZOOM_THRESHOLD = 11; // Lower threshold = radius circles show earlier for privacy
    const shouldShowRadiusCircles = canRenderRadius && currentZoom >= RADIUS_ZOOM_THRESHOLD;

    if (losAngelesOnly) {
      if (markerCacheRef.current.size) {
        markerCacheRef.current.forEach(entry => entry.marker.remove());
        markerCacheRef.current.clear();
      }
      removeRadiusLayers();
      const laCenter: [number, number] = [-118.243683, 34.052235];
      const first = validPreceptors[0];
      if (first) {
        const fallbackLabel = (first.name || '').split(' ').slice(0, 2).map(part => part[0]).join('').toUpperCase() || 'LA';
        const markerEl = showPhotos
          ? createPhotoEl(first.photoUrl, first.name, fallbackLabel, photoSize)
          : createBasePinEl(first.rotationCode || 'LA', markerColor, pinStyle);
        const marker = new mapboxgl.Marker({ element: markerEl, anchor: pinStyle === 'pin' ? 'bottom' : 'center' })
          .setLngLat(laCenter)
          .addTo(map);
        markersRef.current.push(marker);
      }
    } else if (clusterCentroidMode && centerOverride) {
      if (markerCacheRef.current.size) {
        markerCacheRef.current.forEach(entry => entry.marker.remove());
        markerCacheRef.current.clear();
      }
      removeRadiusLayers();
      const count = preceptors.length;
      const label = clusterLabel || count.toString();
      const wrap = document.createElement('div');
      wrap.className = 'relative group';
      const head = document.createElement('div');
      head.className = 'w-10 h-10 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shadow ring-1 ring-black/10 bg-red-600';
      head.textContent = label;
      const tail = document.createElement('div');
      tail.className = 'absolute left-1/2 -bottom-[6px] w-0 h-0 -translate-x-1/2';
      tail.style.borderLeft = '8px solid transparent';
      tail.style.borderRight = '8px solid transparent';
      tail.style.borderTop = '10px solid #dc2626';
      wrap.appendChild(head);
      wrap.appendChild(tail);
      const marker = new mapboxgl.Marker({ element: wrap, anchor: 'bottom' })
        .setLngLat(centerOverride)
        .addTo(map);
      markersRef.current.push(marker);
    } else if (shouldShowRadiusCircles) {
      if (markerCacheRef.current.size) {
        markerCacheRef.current.forEach(entry => entry.marker.remove());
        markerCacheRef.current.clear();
      }
      renderRadiusLayers({
        preceptors: validPreceptors,
        radiusMiles: radiusMiles as number,
        selectedId,
        onMarkerClick,
      });
    } else {
      removeRadiusLayers();
      const shouldShowPins = !radiusMiles || (canRenderRadius && currentZoom < RADIUS_ZOOM_THRESHOLD);
      if (shouldShowPins) {
        const nextIds = new Set(validPreceptors.map(p => p.id));
        markerCacheRef.current.forEach((entry, id) => {
          if (!nextIds.has(id)) {
            entry.marker.remove();
            markerCacheRef.current.delete(id);
          }
        });

        validPreceptors.forEach((p, idx) => {
          const numericLabel = (idx + 1).toString();
          const label = showIndices ? numericLabel : '';
          const fallbackLabel = (p.name || '').split(' ').slice(0, 2).map(part => part[0]).join('').toUpperCase() || numericLabel;
          const isSelected = p.id === selectedId;
          const color = isSelected ? '#1d4ed8' : markerColor; // Darker blue when selected

          const cached = markerCacheRef.current.get(p.id);
          if (!cached) {
            const markerEl = showPhotos
              ? createPhotoEl(p.photoUrl, p.name, fallbackLabel, photoSize)
              : createBasePinEl(label, color, pinStyle, isSelected);
            if (!showPhotos) markerEl.classList.add('cursor-pointer');
            markerEl.onclick = () => onMarkerClick?.(p.id);

            // Tooltip on click disabled - just select the item in the list

            const marker = new mapboxgl.Marker({ element: markerEl, anchor: showPhotos ? 'center' : (pinStyle === 'pin' ? 'bottom' : 'center') })
              .setLngLat([p.longitude!, p.latitude!])
              .addTo(map);
            markerCacheRef.current.set(p.id, { marker, el: markerEl });
          } else {
            cached.marker.setLngLat([p.longitude!, p.latitude!]);
            if (!showPhotos) {
              updatePinElement(cached.el, label, color, pinStyle, isSelected);
            }
          }
        });
        
        // Pan to selected marker only when selection changes
        if (selectedId && selectedId !== lastSelectedIdRef.current) {
          const selectedPreceptor = validPreceptors.find(p => p.id === selectedId);
          if (selectedPreceptor?.longitude && selectedPreceptor?.latitude) {
            map.easeTo({
              center: [selectedPreceptor.longitude, selectedPreceptor.latitude],
              duration: 500,
            });
          }
        }
        lastSelectedIdRef.current = selectedId;
      }
    }

    extraPins.forEach(pin => {
      const el = document.createElement('div');
      el.className = 'flex items-center justify-center rounded-full text-white text-[10px] font-bold shadow-md ring-2 ring-white cursor-pointer';
      el.style.width = '24px';
      el.style.height = '24px';
      el.style.backgroundColor = pin.color || markerColor;
      if (pin.label) el.textContent = pin.label;
      const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat([pin.lng, pin.lat])
        .addTo(map);
      extraMarkersRef.current.push(marker);
    });

    if (userLocation) {
      const el = document.createElement('div');
      el.className = 'relative flex items-center justify-center pointer-events-none z-[999]';
      const pinWrap = document.createElement('div');
      pinWrap.className = 'relative';
      const head = document.createElement('div');
      head.className = 'w-8 h-8 rounded-full bg-red-600 ring-2 ring-white shadow-lg';
      const tail = document.createElement('div');
      tail.className = 'absolute left-1/2 -bottom-[10px] w-0 h-0 -translate-x-1/2';
      tail.style.borderLeft = '8px solid transparent';
      tail.style.borderRight = '8px solid transparent';
      tail.style.borderTop = '12px solid #dc2626';
      pinWrap.appendChild(head);
      pinWrap.appendChild(tail);
      el.appendChild(pinWrap);
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(map);
      extraMarkersRef.current.push(marker);
    }

    if (autoFit && !centerOverride && !zipBounds && validPreceptors.length > 1 && !selectedId && !userInteractedRef.current) {
      const bounds = new mapboxgl.LngLatBounds();
      validPreceptors.forEach(p => {
        bounds.extend([p.longitude!, p.latitude!]);
      });
      map.fitBounds(bounds, { padding: 60, maxZoom: detailView ? 16 : 15 });
      if (map.getZoom() < 12) map.setZoom(12);
      if (detailView && map.getZoom() < 15) map.setZoom(15);
      if (freezeZoom) {
        const finalZ = fixedZoomLevel ?? map.getZoom();
        map.setZoom(finalZ);
        map.setMinZoom(finalZ);
        map.setMaxZoom(finalZ);
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();
        map.dragRotate?.disable();
        if (disablePan) map.dragPan?.disable();
      }
    }
  }, [
    mapRef,
    mapLoaded,
    preceptors,
    selectedId,
    showPhotos,
    photoSize,
    pinStyle,
    markerColor,
    showIndices,
    radiusMiles,
    currentZoom,
    clusterCentroidMode,
    losAngelesOnly,
    centerOverride,
    zipBounds,
    autoFit,
    detailView,
    freezeZoom,
    fixedZoomLevel,
    disablePan,
    userLocation,
    extraPins,
    onMarkerClick,
    showTooltipForPreceptor,
    scheduleHideTooltip,
    removeRadiusLayers,
    renderRadiusLayers,
  ]);

  useEffect(() => {
    return () => {
      markerCacheRef.current.forEach(entry => entry.marker.remove());
      markerCacheRef.current.clear();
      markersRef.current.forEach(m => m.remove());
      extraMarkersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      extraMarkersRef.current = [];
    };
  }, []);
};
